import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getErrorMessage } from '@/lib/api-error';

// Error toasts are shown in ONE place — the caches below — so a failed request never
// produces two toasts. Hooks don't toast errors themselves; they pass a context-specific
// fallback via `meta: { errorMessage }`, used only when the API sends no message.
// `meta: { suppressErrorToast: true }` opts out for callers that render the error inline.
interface AppMeta extends Record<string, unknown> {
  errorMessage?: string;
  suppressErrorToast?: boolean;
}

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: AppMeta;
    mutationMeta: AppMeta;
  }
}

function toastError(error: unknown, meta: AppMeta | undefined) {
  if (meta?.suppressErrorToast) return;
  toast.error(getErrorMessage(error, meta?.errorMessage));
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 10,
      retry: (failureCount, error) => {
        if (
          error instanceof Error &&
          'status' in error &&
          typeof error.status === 'number' &&
          // 429: retrying straight away only makes the rate limit worse
          [401, 403, 404, 429].includes(error.status)
        ) {
          return false;
        }
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      toastError(error, query.meta);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      toastError(error, mutation.meta);
    },
  }),
});
